import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';
import { getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import { reloadCurrentClient } from '$/store/client/actions';
import {
  deleteClientCloDealManager,
  addClientCloDealManager,
  getAllCloDealManagers,
} from '$/store/cloDealManager/actions';
import { AgGridReact } from 'ag-grid-react';
import ForbiddenCharFilter from '$/components/global/floatingFilters/ForbiddenCharFilter';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import DeleteButton from '$/components/grid/DeleteButton';

interface IClientCloDealAccessProps {
  client?: Bixby.Models.Client;
  managers?: Bixby.Models.CloDealManager[] | undefined;
  isSuperAdmin?: boolean;
  dispatchGetCloManagers: () => Promise<void>;
  dispatchAddClientCloManager: (clientId: string, managerId: string) => Promise<void>;
  dispatchReloadCurrentClient: () => Promise<void>;
  dispatchDeleteClientCloManager: (clientId: string, managerId: string) => Promise<void>;
}

interface DeleteManagerModel {
  active: boolean;
  managerId: string | undefined;
  managerName: string | undefined;
}

const initialDeleteManagerState = {
  active: false,
  managerId: undefined,
  managerName: undefined,
};

const ClientCloDealPermissioning: React.FC<IClientCloDealAccessProps> = props => {
  const {
    client,
    managers,
    dispatchGetCloManagers,
    dispatchAddClientCloManager,
    isSuperAdmin,
    dispatchDeleteClientCloManager,
  } = props;
  const [orderedManagers, setOrderedManagers] = useState(managers as Bixby.Models.CloDealManager[]);
  const [manager, setManager] = useState(undefined as Bixby.Models.CloDealManager | undefined);
  const [addManagerModalActive, setAddManagerModalActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [deleteManagerModal, setDeleteManagerModal] = useState(initialDeleteManagerState as DeleteManagerModel);
  const [filteredManagers, setFilteredManagers] = useState([] as Bixby.Models.CloDealManager[]);

  const clientCloDealManagers = useMemo(
    () => (client && client.cloDealManagers ? client.cloDealManagers : undefined),
    [client]
  );

  useEffect(() => {
    const getCloMangerDispatcher = async () => {
      await dispatchGetCloManagers();
    };
    getCloMangerDispatcher();
  }, [dispatchGetCloManagers]);

  useEffect(() => {
    setOrderedManagers(
      Bixby.Utils.orderBy<Bixby.Models.CloDealManager>(managers, c => c.name).filter(clo => clo.managerName !== null)
    );
  }, [managers]);

  const handleAddManager = async () => {
    if (client && manager) {
      setIsLoading(true);
      await dispatchAddClientCloManager(client.id, manager.id);
    }
    setAddManagerModalActive(false);
    setIsLoading(false);
  };

  const handleOpenConfirmAddManager = () => {
    setAddManagerModalActive(true);
  };

  const handleCancelAddManager = () => {
    setAddManagerModalActive(false);
  };

  const handleManagerChange = (ev: React.FormEvent<HTMLInputElement>) => {
    const selected = managers?.find(c => c.id === ev.currentTarget.value);

    setManager(selected);
  };

  const handleDeleteManager = async () => {
    if (client && (deleteManagerModal || ({} as DeleteManagerModel)).managerId) {
      await dispatchDeleteClientCloManager(client.id, deleteManagerModal.managerId || '');
    }

    setDeleteManagerModal({ ...initialDeleteManagerState });
  };

  const handleOpenConfirmDeleteManager = (row: Bixby.Models.CloDealManager) => {
    setDeleteManagerModal({
      active: true,
      managerId: row.manager_Id,
      managerName: row.managerName,
    });

    return Promise.resolve();
  };

  const handleCancelDeleteManager = () => {
    setDeleteManagerModal({ ...initialDeleteManagerState });
  };

  const deleteButtonCellRenderer = (props: any) => {
    const handleDeleteButton = () => {
      handleOpenConfirmDeleteManager(props.data);
      return Promise.resolve();
    };
    return <DeleteButton row={props.data} deleter={handleDeleteButton} />;
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getExactMatchDataList = (evt: any) => {
    const input = evt.target.value;
    if (!input) {
      setFilteredManagers(orderedManagers);
      return;
    }

    const filtered: Bixby.Models.CloDealManager[] | [] =
      orderedManagers.filter(
        c =>
          `${c.managerId}`.indexOf(input) != -1 ||
          (c.managerName ?? '').toLowerCase().indexOf((input ?? '').toLowerCase()) != -1
      ) || orderedManagers;

    setFilteredManagers(filtered);
  };

  const fillDataListIfEmpty = (evt: any) => {
    const input = evt.target.value;

    if (!input) {
      setFilteredManagers(orderedManagers);
    }
  };

  return client ? (
    <section className="client_cloaccess">
      {clientCloDealManagers && !Bixby.Utils.isNullOrEmpty(clientCloDealManagers) ? (
        <div className="client_cloaccess_datagrid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                defaultColDef={{
                  flex: 1,
                  minWidth: 150,
                  resizable: true,
                  sortable: true,
                  lockPinned: true,
                }}
                onGridReady={onGridReady}
                rowData={clientCloDealManagers}
                suppressDragLeaveHidesColumns={true}
                suppressClipboardPaste={true}
                suppressMovableColumns={true}
                getContextMenuItems={getContextMenuItems}
                animateRows={true}
                headerHeight={32}
                rowHeight={32}
                components={{ forbiddenCharFilter: ForbiddenCharFilter }}
                columnDefs={[
                  { minWidth: 50, cellRenderer: deleteButtonCellRenderer },
                  {
                    field: 'managerId',
                    headerName: 'Manager Id',
                    minWidth: 290,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    floatingFilterComponent: 'forbiddenCharFilter',
                  },
                  {
                    field: 'managerName',
                    headerName: 'Manager Name',
                    minWidth: 290,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    floatingFilterComponent: 'forbiddenCharFilter',
                  },
                  {
                    field: 'managerShortName',
                    headerName: 'Manager Short Name',
                    minWidth: 290,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    floatingFilterComponent: 'forbiddenCharFilter',
                  },
                ]}></AgGridReact>
            </div>
          </div>
        </div>
      ) : (
        <p className="space_around">{`There are no managers associated with ${client.name}.`}</p>
      )}
      {isSuperAdmin ? (
        <div className="add_clo">
          <h3>Add a Manager</h3>
          <InputBlock title={`Associate an existing manager with ${client.name}`}>
            <input
              list="clos"
              name="clo"
              onChange={handleManagerChange}
              onInput={getExactMatchDataList}
              onFocus={fillDataListIfEmpty}
              autoComplete="off"
            />
            <datalist id="clos">
              {(filteredManagers ?? []).map(({ id, managerId, managerName }) => (
                <option key={id} label={`${managerId} - ${managerName}`} value={id}>
                  {managerName}
                </option>
              ))}
            </datalist>
          </InputBlock>
          <BixButton
            type="button"
            disabled={!Bixby.Utils.isDefined(manager) || !isSuperAdmin}
            onClick={handleOpenConfirmAddManager}>
            Add to Client
          </BixButton>
          <Modal title="Add to Client" active={addManagerModalActive} onClose={handleCancelAddManager}>
            <p>
              Add {(manager || ({} as any)).managerName} to {client.name}?
            </p>
            <div className="button_side_wrap">
              <BixButton type="button" onClick={handleAddManager} disabled={isLoading}>
                Add {(manager || ({} as any)).name}
              </BixButton>
              <BixButton light type="button" onClick={handleCancelAddManager}>
                Cancel
              </BixButton>
            </div>
          </Modal>
        </div>
      ) : null}
      <Modal
        title="Remove Manager Access"
        active={(deleteManagerModal || ({} as DeleteManagerModel)).active || false}
        onClose={handleCancelDeleteManager}>
        <p>Remove manager {(deleteManagerModal || ({} as DeleteManagerModel)).managerName}?</p>
        <div className="button_side_wrap">
          <BixButton type="button" onClick={handleDeleteManager}>
            Remove {(deleteManagerModal || ({} as DeleteManagerModel)).managerName}
          </BixButton>
          <BixButton light type="button" onClick={handleCancelDeleteManager}>
            Cancel
          </BixButton>
        </div>
      </Modal>
    </section>
  ) : (
    <></>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCloManagers: () => dispatch(getAllCloDealManagers()),
  dispatchAddClientCloManager: (clientId: string, managerId: string) =>
    dispatch(addClientCloDealManager(clientId, managerId, () => dispatch(reloadCurrentClient()))),
  dispatchDeleteClientCloManager: (clientId: string, managerId: string) =>
    dispatch(deleteClientCloDealManager(clientId, managerId, () => dispatch(reloadCurrentClient()))),
  dispatchReloadCurrentClient: () => dispatch(reloadCurrentClient()),
});
const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  return {
    isSuperAdmin,
    client: state.clientState.currentClient,
    managers: state.cloDealManagerState.cloDealManagers,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientCloDealPermissioning);
